import { theme } from '@/components';
import { getBudgetGraphColor } from '@/features/project/_utils/budget-graph-color';
import type { BudgetAmountModel } from '@/features/project/projects/_models/project';
import { useLocaledAmount } from '@/providers';
import { formatValueByUnit } from '@/utils';
import Highcharts from 'highcharts';
import { HighchartsReact } from 'highcharts-react-official';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type BudgetAmountType = z.infer<typeof BudgetAmountModel>;

type Props = {
  budgetAmountData: BudgetAmountType[];
};

export const BudgetAmountGraph: FC<Props> = ({ budgetAmountData }) => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();

  const initialBudgetsTotalAmount = budgetAmountData.flatMap(
    (item) => item.initialBudgetsTotalAmount ?? [],
  );

  const budgetStartRange = budgetAmountData.find(
    (item) => item.initialBudgetsTotalAmount !== null,
  )?.fiscalYear;

  const graphSeriesColor = getBudgetGraphColor(initialBudgetsTotalAmount, {
    consistent: token.controlItemBgActive,
    decrease: token.geekblue5,
    increase: token.orange6,
  });

  const graphOptions = {
    chart: {
      type: 'area',
      width: 150,
      height: 80,
      backgroundColor: 'transparent',
    },
    title: {
      text: '',
    },
    credits: {
      enabled: false,
    },
    legend: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    series: [
      {
        type: 'area',
        name: 'Amounts',
        yAxis: 0,
        data: [...initialBudgetsTotalAmount],
        marker: {
          lineWidth: 1,
          lineColor: graphSeriesColor,
          radius: 4,
          fillColor: graphSeriesColor,
        },
      },
    ],
    plotOptions: {
      area: {
        color: graphSeriesColor,
        fillColor: {
          linearGradient: {
            x1: 0,
            y1: 0,
            x2: 0,
            y2: 1,
          },
          stops: [
            [
              0,
              getBudgetGraphColor(initialBudgetsTotalAmount, {
                consistent: token.colorInfoBg,
                decrease: token.geekblue3,
                increase: token.orange5,
              }),
            ],
            [
              1,
              getBudgetGraphColor(initialBudgetsTotalAmount, {
                consistent: token.colorBgLayout,
                decrease: token.geekblue1,
                increase: token.orange1,
              }),
            ],
          ],
        },
      },
      series: {
        label: {
          connectorAllowed: false,
        },
        pointStart: Number(budgetStartRange),
      },
    },
    tooltip: {
      headerFormat: `<b>{point.x}${t('search-year')}</b><br/>`,
      distance: 10,
      pointFormatter: function (this: Highcharts.Point) {
        return `<b>${
          this.y
            ? formatValueByUnit(this.y, {
                operation: 'round',
                unit,
              })
            : 0
        }${t('thousand-yen-unit')}</b>`;
      },
    },
    xAxis: {
      lineWidth: 0,
      gridLineWidth: 0,
      labels: {
        enabled: false,
      },
      visible: false,
      ordinal: false,
    },
    yAxis: {
      lineWidth: 0,
      gridLineWidth: 0,
      labels: {
        enabled: false,
      },
      visible: false,
      tickInterval: 10,
      margin: 10,
    },
  };
  return <HighchartsReact highcharts={Highcharts} options={graphOptions} />;
};
